import { useState, useRef, useEffect } from "react";
import { Info } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { getMetricHelp } from "@/lib/metricHelp";

/**
 * MetricHelp — inline contextual help for KPI cards, category tiles,
 * and executive dashboard widgets.
 *
 * Usage:
 *   <MetricHelp topicKey="compliance_score" />
 *   <MetricHelp topicKey="backup_last_success" size="sm" side="top" />
 *
 * Behaviour:
 *   * Renders a subtle ⓘ button next to a metric label.
 *   * Opens on click (touch-friendly) AND on hover after 250ms delay.
 *   * Closes on click-outside, Escape, or mouseleave (with 200ms grace).
 *   * Keyboard: Tab focuses the icon, Enter/Space toggles.
 *   * Screen readers: aria-label describes the topic; content region has role="tooltip".
 *   * Auto-repositions via Radix Popover (portal + collision detection).
 *
 * Design:
 *   * Icon: 14px lucide `Info` at neutral slate-400, hover slate-600.
 *   * Panel: 320px wide, HarborWell design tokens, sections with subtle dividers.
 */
export default function MetricHelp({
  topicKey,
  content: contentOverride,   // optional inline content override
  size = "sm",                 // sm | md
  side = "bottom",             // top | bottom | left | right
  align = "start",             // start | center | end
  className = "",
  testId,
}) {
  const [open, setOpen] = useState(false);
  const hoverTimer = useRef(null);
  const closeTimer = useRef(null);

  const content = contentOverride || getMetricHelp(topicKey);

  useEffect(() => () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  if (!content) return null;

  const openOnHover = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setOpen(true), 250);
  };
  const closeOnLeave = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 200);
  };

  const iconSize = size === "md" ? "w-4 h-4" : "w-3.5 h-3.5";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId || `metric-help-${topicKey}`}
          aria-label={`Help: ${content.title}`}
          onMouseEnter={openOnHover}
          onMouseLeave={closeOnLeave}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          className={`inline-flex items-center justify-center rounded-full text-slate-400 hover:text-[#1E3A8A] focus:outline-none focus:ring-2 focus:ring-[#7E937D]/50 focus:text-[#1E3A8A] transition-colors ml-1 align-middle ${className}`}
        >
          <Info className={iconSize} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        sideOffset={6}
        collisionPadding={12}
        onMouseEnter={() => {
          if (closeTimer.current) clearTimeout(closeTimer.current);
        }}
        onMouseLeave={closeOnLeave}
        role="tooltip"
        className="w-80 max-w-[calc(100vw-2rem)] bg-white border border-[#E5E5E0] rounded-xl shadow-xl p-0 text-slate-800"
        data-testid={`metric-help-panel-${topicKey}`}
      >
        <HelpPanel content={content} />
      </PopoverContent>
    </Popover>
  );
}

function HelpPanel({ content }) {
  return (
    <div className="text-xs leading-relaxed">
      {/* Title */}
      <div className="px-4 pt-3 pb-2 border-b border-[#F1F0EA]">
        <div className="text-[10px] uppercase tracking-[0.14em] text-[#1E3A8A] font-semibold">
          About this metric
        </div>
        <div className="mt-1 text-sm font-semibold text-slate-900">
          {content.title}
        </div>
      </div>

      {/* What */}
      {content.what && (
        <Section label="What it shows">
          <p>{content.what}</p>
        </Section>
      )}

      {/* Why */}
      {content.why && (
        <Section label="Why it matters">
          <p>{content.why}</p>
        </Section>
      )}

      {/* How */}
      {content.how && (
        <Section label="How it's calculated">
          {Array.isArray(content.how) ? (
            <ul className="list-disc pl-4 space-y-0.5">
              {content.how.map((h, i) => <li key={i}>{h}</li>)}
            </ul>
          ) : (
            <p>{content.how}</p>
          )}
        </Section>
      )}

      {/* Affects */}
      {content.affects && content.affects.length > 0 && (
        <Section label="What affects the score">
          <ul className="list-disc pl-4 space-y-0.5">
            {content.affects.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </Section>
      )}

      {/* Recommended actions */}
      {content.recommendedActions && content.recommendedActions.length > 0 && (
        <Section label="If the value is low">
          <ul className="list-disc pl-4 space-y-0.5">
            {content.recommendedActions.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </Section>
      )}

      {/* Cadence */}
      {content.cadence && (
        <div className="px-4 py-2 border-t border-[#F1F0EA] bg-[#F9F8F6] rounded-b-xl">
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
            Update cadence
          </div>
          <div className="mt-0.5 text-slate-700">{content.cadence}</div>
        </div>
      )}
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div className="px-4 py-2 border-b border-[#F1F0EA] last:border-b-0">
      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1">
        {label}
      </div>
      <div className="text-slate-700">{children}</div>
    </div>
  );
}
